import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';

const features = [
  { title: 'Drag-and-drop builder', body: 'Pixel-precise editing with real-time preview across desktop, tablet, mobile.' },
  { title: 'AI page generation', body: 'Describe a page in plain English. Pubvora drafts it block by block.' },
  { title: 'Built-in commerce', body: 'Products, checkout, coupons, subscriptions — without third-party plugins.' },
  { title: 'Membership & courses', body: 'Gate content, sell cohorts, and run bookings from the same dashboard.' },
  { title: 'Hosting & CDN', body: 'Cloud hosting with auto-scaling, global edge cache, and zero-config TLS.' },
  { title: 'Developer-friendly', body: 'Headless CMS, REST + Webhooks, custom code injection, and Git integration.' },
];

export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-ink-950 text-ink-50">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2 text-lg font-bold">
          <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand-500 text-white">P</span>
          <span>Pubvora</span>
        </div>
        <nav className="flex items-center gap-2">
          {user ? (
            <Link to="/sites" className="btn-primary">Open dashboard</Link>
          ) : (
            <>
              <Link to="/login" className="text-sm text-ink-200 hover:text-white">Log in</Link>
              <Link to="/register" className="btn-primary">Get started</Link>
            </>
          )}
        </nav>
      </header>

      <section className="mx-auto max-w-6xl px-6 pb-24 pt-12 lg:pt-24">
        <div className="max-w-3xl">
          <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-ink-800 bg-ink-900/60 px-3 py-1 text-xs uppercase tracking-wider text-ink-300">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-400" />
            v0 · early preview
          </p>
          <h1 className="text-4xl font-extrabold leading-tight md:text-6xl">
            Build, launch, and grow an entire online business.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-ink-300">
            Pubvora is the no-code platform for shipping the whole stack — site, store, courses,
            bookings, marketing — from one editor. Generate pages with AI, customize every block,
            publish in one click.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={user ? '/sites' : '/register'} className="btn-primary px-6 py-3 text-base">
              Start building free
            </Link>
            <a
              href="#features"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-ink-800 px-6 py-3 text-sm font-semibold text-ink-200 hover:bg-ink-900"
            >
              Explore features
            </a>
          </div>
        </div>
      </section>

      <section id="features" className="border-t border-ink-900 bg-ink-950">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <h2 className="text-3xl font-bold">Everything you need to ship a business</h2>
          <p className="mt-3 max-w-2xl text-ink-300">
            12 product categories. One workspace. Pubvora replaces the patchwork of tools most
            founders end up duct-taping together.
          </p>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl border border-ink-900 bg-ink-900/40 p-6 transition hover:border-ink-800 hover:bg-ink-900/70"
              >
                <h3 className="text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-ink-300">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-ink-900 py-10 text-center text-xs text-ink-500">
        © {new Date().getFullYear()} Pubvora. A platform to build, launch, and grow an entire online business.
      </footer>
    </div>
  );
}
